import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import ReturnButtons from '../../components/core/ReturnButtons';
import { loadScript } from '../../../services/dynamic-script';
import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { useThemeMode } from '../../contexts/index.jsx';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_BOTTOM_SAFE_AREA,

  OVERLAY_COLUMN_LAYOUT,
} from '../Shoreline/theme.js';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';

import * as turf from '@turf/turf';

import { useDispatch, useSelector } from 'react-redux';
import * as Map from '../../../common/map';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import { Actions as MapActions } from '../../../store/ducks/map';
import { ConcreteLayer, Layer } from '../../../common/classes';

import LayerControler from '../../components/mepbay/LayerControler';
import SidePanel from '../../components/mepbay/SidePanel';
import { createMarkerIcon } from '../../components/mepbay/utils/markerUtils';
import { drawAngleAndR0 } from '../../components/mepbay/utils/drawAngleAndR0';
import { calculateWithTurf } from '../../../algorithms/mepbay/parabolicCalculations';
import { exportGeoJson } from '../../../algorithms/mepbay/geoJsonExport';
import { exportSession, useLocalStorage } from '../../../common/utils';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport } from '../../contexts/index.jsx';
import { buildSessionExport, readPendingMepBaySession } from '../../../algorithms/mepbay/sessionExport';
import { buildMepBayReport, REPORT_MODULE } from '../../../algorithms/mepbay/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';
import { aoiPaths } from '../../../common/aoi';
const roiColor = '#00A391';

/**
 * @function ProcessingPage
 * @description MEPBay processing page. Shows the map with the ROI and acquired images, lets the
 * user create parabolic-bay model layers by picking three control points (diffraction, coastal
 * control, predominant wave) per layer, computes each layer's parabolic model, and manages
 * layer visibility/opacity/deletion via LayerControler and SidePanel.
 * @see {@link GoogleMap} - map initialization, via `common/map`.
 * @see {@link calculateParabolicModel} - runs the parabolic bay calculation for a layer.
 * @see {@link LayerControler} - layers management menu.
 * @see {@link SidePanel} - step-by-step wizard panel.
 * **Called by:
 * @see {MainPage} - via the `/mepbay/result` Route (imported as ProcessingMepBayPage).
 * @memberof module:Mepbay
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingPage = () => {
  const { t } = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const dispatch = useDispatch();
  const coordinates = useSelector((state) => state.mepbay.coordinates);
  const satelliteIndex = useSelector((state) => state.mepbay.satelliteIndex);
  const availableDates = useSelector((state) => state.mepbay.availableDates);
  const images = useSelector((state) => state.imagery.images);
  const nextLayerId = useSelector((state) => state.imagery.layerId);
  const classes = useStyles();
  const mapDiv = useRef();
  const mapRef = useRef();
  const layersRef = useRef({});
  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const mepbayTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );
  const themeModeRef = useRef(shorelineThemeMode);
  const [mapType, setMapType] = useLocalStorage('mepbayMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('mepbayMapLabels', true);
  const labelsVisibleRef = useRef(labelsVisible);

  /**
   * @function applyMapThemeStyle
   * @description Recomputes the live map's `styles` array from the module theme mode, the
   * map's current base layer and the "Marcadores" toggle. Dark styling applies only when the
   * mode is dark AND the base layer is roadmap/terrain — satellite/hybrid imagery is never
   * recolored. Reads both inputs through refs because this also runs from the map's own
   * `maptypeid_changed` listener, which is registered once and would otherwise close over the
   * values they had at mount.
   * @see {@link buildMapStyles}
   * **Called by:
   * @see {ProcessingPage} - on theme-mode / labels change, and via the map's maptypeid_changed listener.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const applyMapThemeStyle = () => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({
      styles: buildMapStyles({
        themeMode: themeModeRef.current,
        mapType: mapRef.current.getMapTypeId(),
        labelsVisible: labelsVisibleRef.current,
      }),
    });
  };

  /**
   * @function handleMapTypeChange
   * @description Switches the live Google Maps instance's base layer from the compact "Mapa de
   * fundo" panel, without MepBay's old native map-type control (now disabled).
   * @param {String} type - A Google Maps `mapTypeId` (`'roadmap'`, `'satellite'`, `'hybrid'`, or
   * `'terrain'`).
   * **Called by:
   * @see {ProcessingPage} - via `BackgroundMapPanel`'s `onMapTypeChange`.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    themeModeRef.current = shorelineThemeMode;
    labelsVisibleRef.current = labelsVisible;
    applyMapThemeStyle();
  }, [shorelineThemeMode, labelsVisible]);

  /**
   * @function displayROI
   * @description Registers the ROI as a (non-interactive) layer entry and draws it as a polygon
   * on the map, then fits the map view to its bounds.
   * **Called by:
   * @see {initializeMap}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const displayROI = () => {
    if (coordinates && Array.isArray(coordinates) && coordinates.length > 0) {
      const coordinatesLatLng = aoiPaths(coordinates);
      const newLayerId = `roi_layer_${layerCounter + 1}`;
      const roiLayer = {
        id: newLayerId,
        name: `ROI ${layerCounter + 1}`,
        color: roiColor,
        visible: true,
        opacity: 0.6,
        controlPoints: {
          diffractionPoint: null,
          coastalControlPoint: null,
          predominantWavePoint: null,
        },
        markers: {},
        lines: [],
        completed: true,
        isROI: true,
      };

      layersRef.current[newLayerId] = roiLayer;

      setLayers((prev) => ({ ...prev, [newLayerId]: roiLayer }));
      setLayerCounter((prev) => prev + 1);

      const roiPolygon = new window.google.maps.Polygon({
        paths: coordinatesLatLng,
        geodesic: true,
        fillColor: roiColor,
        fillOpacity: 0.2,
        strokeColor: roiColor,
        strokeOpacity: 0.6,
        strokeWeight: 3,
        clickable: false,
      });

      roiPolygon.setMap(mapRef.current);
      roiLayer.polygon = roiPolygon;

      const bounds = new window.google.maps.LatLngBounds();
      coordinatesLatLng.flat().forEach((coord) => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const [layers, setLayers] = useState({});
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [layerCounter, setLayerCounter] = useState(0);
  const [controlPoints, setControlPoints] = useState({
    diffractionPoint: null,
    coastalControlPoint: null,
    predominantWavePoint: null,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSelectingPoint, setIsSelectingPoint] = useState(false);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [viewDataMenuAnchor, setViewDataMenuAnchor] = useState(null);

  useEffect(() => {
    const values = Object.values(images);
    if (values.length > 0 && mapRef.current && window.ee) {
      values.forEach((image, i) => {
        const hasLayers = image.layers && Object.keys(image.layers).length > 0;
        if (image.imageObject && !image._overlayAdded && !hasLayers) {
          try {
            /**
             * @function addImageAsync
             * @description Renders the acquired image as a true-color Earth Engine tile overlay
             * and pushes it as a layer, marking the image as having its overlay added.
             * **Called by:
             * @see {ProcessingPage} - via the images-loaded effect, for each newly acquired image.
             * @memberof module:Mepbay
             * @returns {Promise<void>}
             */
            const addImageAsync = async () => {
              try {
                const visParams = {
                  bands: ['B4', 'B3', 'B2'],
                  min: 0,
                  max: 3000,
                  gamma: 1.4,
                };

                const mapId = await new Promise((resolve, reject) => {
                  try {
                    image.imageObject.getMap(visParams, (result, error) => {
                      if (error) {
                        console.error('Earth Engine getMap error:', error);
                        reject(error);
                      } else {
                        resolve(result);
                      }
                    });
                  } catch (syncError) {
                    console.error('Synchronous error in getMap:', syncError);
                    reject(syncError);
                  }
                });

                const source = new window.ee.layers.EarthEngineTileSource(mapId);
                const overlay = new window.ee.layers.ImageOverlay(source);
                const layerId = nextLayerId;
                const baseLayer = new Layer(image.imageObject, `${image.name}`, {
                  bands: ['B4', 'B3', 'B2'],
                  min: 0,
                  max: 3000,
                  gamma: 1.4,
                });
                const concreteLayer = new ConcreteLayer(
                  baseLayer,
                  overlay,
                  null,
                  null,
                  i === values.length - 1 ? 1 : 0,
                  true,
                );
                const parentId = Object.keys(images).find((key) => images[key] === image);
                dispatch(Imagery.pushLayer(concreteLayer, parentId));
                dispatch(MapActions.addEELayer(overlay, layerId, 0));
                image._overlayAdded = true;
                image.overlay = overlay;
              } catch (error) {
                console.error('Error in async image processing:', error, image);
              }
            };

            addImageAsync();
          } catch (error) {
            console.error('Error adding image to map:', error, image);
          }
        }
      });
    } else if (values.length > 0 && !window.ee) {
      console.warn('Earth Engine not available, cannot add images to map');
    }
  }, [images]);

  useEffect(() => {
    if (activeLayerId && layersRef.current[activeLayerId]) {
      const activeLayer = layersRef.current[activeLayerId];
      setControlPoints(activeLayer.controlPoints);
    }
  }, [activeLayerId]);

  /**
   * @function addDragListeners
   * @description Wires hover (cursor feedback), drag-start (clears the angle/R0 overlays), drag
   * (redraws the basic connecting lines live), and drag-end (redraws the final lines and, once
   * all three control points exist, recalculates the parabolic model) listeners on a control
   * point marker.
   * @param {google.maps.Marker} marker - The marker to add drag listeners to.
   * @param {String} pointKey - The control point key (diffractionPoint, coastalControlPoint, or predominantWavePoint).
   * @see {@link drawBasicLines}
   * @see {@link drawLines}
   * @see {@link calculateParabolicModel}
   * **Called by:
   * @see {handleMapClick}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const addDragListeners = useCallback(
    (marker, pointKey) => {
      if (!activeLayerId) return;
      marker.addListener('mouseover', () => {
        if (!isDraggingMarker && !isCalculating && mapRef.current) {
          mapRef.current.getDiv().style.cursor = 'grab';
        }
      });

      marker.addListener('mouseout', () => {
        if (!isDraggingMarker && !isCalculating && mapRef.current) {
          if (isSelectingPoint) {
            mapRef.current.getDiv().style.cursor = 'crosshair';
          } else {
            mapRef.current.getDiv().style.cursor = '';
          }
        }
      });

      marker.addListener('dragstart', (event) => {
        if (isCalculating) {
          event.stop();
          return;
        }

        const currentLayer = layersRef.current[activeLayerId];
        if (currentLayer) {
          if (currentLayer.markers['angleCA']) {
            currentLayer.markers['angleCA'].setMap(null);
            delete currentLayer.markers['angleCA'];
          }
          if (currentLayer.r0Overlay) {
            currentLayer.r0Overlay.setMap(null);
            currentLayer.r0Overlay = null;
          }
        }

        setIsDraggingMarker(true);
      });

      marker.addListener('drag', (event) => {
        if (isCalculating) {
          return;
        }

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const currentLayer = layersRef.current[activeLayerId];
        if (!currentLayer) return;

        const updatedPoints = {
          ...currentLayer.controlPoints,
          [pointKey]: { lat, lng },
        };

        currentLayer.controlPoints = updatedPoints;
        setControlPoints(updatedPoints);
        drawBasicLines(updatedPoints, activeLayerId);
      });

      marker.addListener('dragend', (event) => {
        if (isCalculating) {
          return;
        }

        setIsDraggingMarker(false);
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const currentLayer = layersRef.current[activeLayerId];
        if (!currentLayer) return;

        if (currentLayer.r0Overlay) {
          currentLayer.r0Overlay.setMap(null);
          currentLayer.r0Overlay = null;
        }

        const updatedPoints = {
          ...currentLayer.controlPoints,
          [pointKey]: { lat, lng },
        };
        currentLayer.controlPoints = updatedPoints;
        setControlPoints(updatedPoints);
        setLayers((prev) => ({
          ...prev,
          [activeLayerId]: {
            ...prev[activeLayerId],
            controlPoints: updatedPoints,
          },
        }));

        drawLines(updatedPoints, activeLayerId);
        const pointsArray = Object.values(updatedPoints).filter(Boolean);
        if (pointsArray.length === 3) {
          const [diffractionPoint, coastalControlPoint, predominantWavePoint] = pointsArray;
          calculateParabolicModel(diffractionPoint, coastalControlPoint, predominantWavePoint, activeLayerId);
        }
      });
    },
    [isDraggingMarker, isSelectingPoint, activeLayerId, isCalculating],
  );

  const steps = [
    {
      key: 'diffractionPoint',
      title: t('forms.mepbay.result.diffractionPoint'),
      description: t('forms.mepbay.result.selectDiffractionPoint'),
      color: '#f44336',
    },
    {
      key: 'coastalControlPoint',
      title: t('forms.mepbay.result.closurePoint'),
      description: t('forms.mepbay.result.selectClosurePoint'),
      color: '#ff9800',
    },
    {
      key: 'predominantWavePoint',
      title: t('forms.mepbay.result.tangentPoint'),
      description: t('forms.mepbay.result.selectTangentPoint'),
      color: '#4caf50',
    },
  ];

  useEffect(() => {
    loadScript('google-charts', 'https://www.gstatic.com/charts/loader.js', () => {
      window.google.charts.load('current', {
        packages: ['corechart', 'bar', 'table'],
      });
    });

    /**
     * @function initWithDelay
     * @description Polls (every second) until the Google Maps API and the map container div are
     * ready, then initializes the map.
     * @see {@link initializeMap}
     * **Called by:
     * @see {ProcessingPage} - on mount, and recursively via its own setTimeout.
     * @memberof module:Mepbay
     * @returns {void}
     */
    const initWithDelay = () => {
      if (window.google && window.google.maps && mapDiv.current) {
        initializeMap();
      } else {
        setTimeout(initWithDelay, 1000);
      }
    };

    setTimeout(initWithDelay, 100);
  }, []);

  /**
   * @function initializeMap
   * @description Initializes the Google Maps instance (centered on Babitonga Bay), draws an
   * empty study-area polygon placeholder, and displays the ROI.
   * @see {@link displayROI}
   * **Called by:
   * @see {initWithDelay}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const initializeMap = () => {
    if (!window.google || !mapDiv.current) {
      return;
    }
    mapRef.current = new window.google.maps.Map(mapDiv.current, {
      zoom: 12,
      center: { lat: -26.277, lng: -48.7335 },
      mapTypeId: mapType,
      scaleControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
      ],
    });
    Map.initializeMap(mapRef.current);

    const studyArea = new window.google.maps.Polygon({
      paths: [],
      geodesic: true,
      fillColor: roiColor,
      fillOpacity: 0.2,
      strokeColor: roiColor,
      strokeOpacity: 0.6,
      strokeWeight: 3,
      clickable: false,
    });

    studyArea.setMap(mapRef.current);

    const bounds = new window.google.maps.LatLngBounds();
    [].forEach((coord) => {
      bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
    });
    mapRef.current.fitBounds(bounds);
    displayROI(mapRef.current);
    restorePendingLayers();
    applyMapThemeStyle();
    window.google.maps.event.addListener(mapRef.current, 'maptypeid_changed', applyMapThemeStyle);
  };

  /**
   * @function handleMapClick
   * @description Places the current wizard step's control point at the clicked location: stores
   * it on the active layer, draws its marker (wiring drag listeners), advances to the next step
   * (or marks the layer complete and triggers the parabolic model calculation once all three
   * points exist), and redraws the connecting lines.
   * @param {google.maps.MapMouseEvent} event - The map click event.
   * @see {@link createMarkerIcon}
   * @see {@link addDragListeners}
   * @see {@link calculateParabolicModel}
   * @see {@link drawLines}
   * **Called by:
   * @see {ProcessingPage} - via the map's click listener, while selecting a control point.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleMapClick = useCallback(
    (event) => {
      if (!isSelectingPoint || !activeLayerId || isCalculating) {
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const currentStepKey = steps[currentStep].key;

      const activeLayer = layersRef.current[activeLayerId];
      if (!activeLayer) return;
      const currentLayerPoints = activeLayer.controlPoints;
      const newControlPoints = {
        ...currentLayerPoints,
        [currentStepKey]: { lat, lng },
      };

      activeLayer.controlPoints = newControlPoints;
      setControlPoints(newControlPoints);

      if (activeLayer.markers[currentStepKey]) {
        activeLayer.markers[currentStepKey].setMap(null);
      }

      const pointKey = currentStepKey;
      const icon = createMarkerIcon(pointKey, activeLayer, activeLayer.opacity);

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapRef.current,
        title: steps[currentStep].title,
        draggable: !isCalculating,
        icon: icon,
      });

      addDragListeners(marker, currentStepKey);

      activeLayer.markers[currentStepKey] = marker;
      setIsSelectingPoint(false);
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        activeLayer.completed = true;
        setLayers((prev) => ({
          ...prev,
          [activeLayerId]: { ...prev[activeLayerId], completed: true },
        }));
        const pointsArray = Object.values(newControlPoints).filter(Boolean);
        if (pointsArray.length === 3) {
          const [diffractionPoint, coastalControlPoint, predominantWavePoint] = pointsArray;
          calculateParabolicModel(diffractionPoint, coastalControlPoint, predominantWavePoint, activeLayerId);
        }
      }
      drawLines(newControlPoints, activeLayerId);
    },
    [isSelectingPoint, currentStep, steps, addDragListeners, activeLayerId, isCalculating],
  );

  useEffect(() => {
    if (mapRef.current && handleMapClick) {
      window.google.maps.event.clearListeners(mapRef.current, 'click');
      mapRef.current.addListener('click', handleMapClick);
    }
  }, [handleMapClick]);

  useEffect(() => {
    if (mapRef.current) {
      const mapDiv = mapRef.current.getDiv();
      if (isCalculating) {
        mapDiv.style.cursor = 'wait';
      } else if (isDraggingMarker) {
        mapDiv.style.cursor = 'grabbing';
      } else if (isSelectingPoint) {
        mapDiv.style.cursor = 'crosshair';
      } else {
        mapDiv.style.cursor = '';
      }
    }
  }, [isSelectingPoint, isDraggingMarker, isCalculating]);

  useEffect(() => {
    if (activeLayerId && layersRef.current[activeLayerId]) {
      const layer = layersRef.current[activeLayerId];
      Object.entries(layer.markers).forEach(([pointKey, marker]) => {
        if (['diffractionPoint', 'coastalControlPoint', 'predominantWavePoint'].includes(pointKey)) {
          marker.setDraggable(!isCalculating);
        }
      });
    }
  }, [isCalculating, activeLayerId]);

  /**
   * @function drawLines
   * @description Draws the two straight lines connecting the layer's control points
   * (diffraction→closure, closure→tangent), clearing any previous lines and derived markers
   * (perpendicular/new/auxiliary points, angle labels) first.
   * @param {Object} points - Object containing the three control points.
   * @param {String} layerId - The ID of the layer to draw on.
   * **Called by:
   * @see {addDragListeners} - on drag end.
   * @see {handleMapClick} - after placing the final control point.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const drawLines = (points, layerId) => {
    if (!layerId || !layersRef.current[layerId]) return;

    const layer = layersRef.current[layerId];

    layer.lines.forEach((line) => line.setMap(null));
    layer.lines = [];

    if (layer.markers['perpendicular']) {
      layer.markers['perpendicular'].setMap(null);
      delete layer.markers['perpendicular'];
    }
    if (layer.markers['newPoint']) {
      layer.markers['newPoint'].setMap(null);
      delete layer.markers['newPoint'];
    }
    if (layer.markers['auxiliaryPoint']) {
      layer.markers['auxiliaryPoint'].setMap(null);
      delete layer.markers['auxiliaryPoint'];
    }
    if (layer.markers['betaAngle']) {
      layer.markers['betaAngle'].setMap(null);
      delete layer.markers['betaAngle'];
    }
    if (layer.markers['angleCA']) {
      layer.markers['angleCA'].setMap(null);
      delete layer.markers['angleCA'];
    }

    const pointsArray = Object.values(points).filter(Boolean);
    if (pointsArray.length === 3) {
      const [diffractionPoint, coastalControlPoint, predominantWavePoint] = pointsArray;

      const line1 = new window.google.maps.Polyline({
        path: [diffractionPoint, coastalControlPoint],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: layer.opacity * 0.8,
        strokeWeight: 4,
        visible: layer.visible,
      });

      const line2 = new window.google.maps.Polyline({
        path: [coastalControlPoint, predominantWavePoint],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: 1,
        strokeWeight: 4,
        visible: layer.visible,
      });

      line1.setMap(mapRef.current);
      line2.setMap(mapRef.current);
      layer.lines.push(line1, line2);

      const turfLine1 = turf.lineString([
        [diffractionPoint.lng, diffractionPoint.lat],
        [coastalControlPoint.lng, coastalControlPoint.lat],
      ]);

      const turfLine2 = turf.lineString([
        [coastalControlPoint.lng, coastalControlPoint.lat],
        [predominantWavePoint.lng, predominantWavePoint.lat],
      ]);

      const bearing1 = turf.bearing(
        [coastalControlPoint.lng, coastalControlPoint.lat],
        [diffractionPoint.lng, diffractionPoint.lat],
      );
      const bearing2 = turf.bearing(
        [coastalControlPoint.lng, coastalControlPoint.lat],
        [predominantWavePoint.lng, predominantWavePoint.lat],
      );
      const angle = Math.abs(bearing2 - bearing1);
    } else if (pointsArray.length === 2) {
      const line = new window.google.maps.Polyline({
        path: [pointsArray[0], pointsArray[1]],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: layer.opacity * 0.8,
        strokeWeight: 4,
        visible: layer.visible,
      });

      line.setMap(mapRef.current);
      layer.lines.push(line);
    }
  };

  /**
   * @function drawBasicLines
   * @description Lightweight version of `drawLines` used while a control point is actively being
   * dragged: draws the straight connecting lines without computing the parabolic model.
   * @param {Object} points - Object containing the control points.
   * @param {String} layerId - The ID of the layer to draw on.
   * @see {@link drawLines}
   * **Called by:
   * @see {addDragListeners} - on drag, for live feedback.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const drawBasicLines = (points, layerId) => {
    if (!layerId || !layersRef.current[layerId]) return;

    const layer = layersRef.current[layerId];
    layer.lines.forEach((line) => line.setMap(null));
    layer.lines = [];

    if (layer.markers['perpendicular']) {
      layer.markers['perpendicular'].setMap(null);
      delete layer.markers['perpendicular'];
    }
    if (layer.markers['betaAngle']) {
      layer.markers['betaAngle'].setMap(null);
      delete layer.markers['betaAngle'];
    }
    if (layer.markers['angleCA']) {
      layer.markers['angleCA'].setMap(null);
      delete layer.markers['angleCA'];
    }
    if (layer.markers['newPoint']) {
      layer.markers['newPoint'].setMap(null);
      delete layer.markers['newPoint'];
    }
    if (layer.markers['auxiliaryPoint']) {
      layer.markers['auxiliaryPoint'].setMap(null);
      delete layer.markers['auxiliaryPoint'];
    }

    const pointsArray = Object.values(points).filter(Boolean);

    if (pointsArray.length === 3) {
      const [diffractionPoint, coastalControlPoint, predominantWavePoint] = pointsArray;
      const line1 = new window.google.maps.Polyline({
        path: [diffractionPoint, coastalControlPoint],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: layer.opacity * 0.8,
        strokeWeight: 4,
        visible: layer.visible,
      });

      const line2 = new window.google.maps.Polyline({
        path: [coastalControlPoint, predominantWavePoint],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: 1,
        strokeWeight: 4,
        visible: layer.visible,
      });

      line1.setMap(mapRef.current);
      line2.setMap(mapRef.current);
      layer.lines.push(line1, line2);
    } else if (pointsArray.length === 2) {
      const line = new window.google.maps.Polyline({
        path: [pointsArray[0], pointsArray[1]],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: layer.opacity * 0.8,
        strokeWeight: 4,
        visible: layer.visible,
      });

      line.setMap(mapRef.current);
      layer.lines.push(line);
    }
  };

  /**
   * @function timer
   * @description Returns a Promise that resolves after the given delay.
   * @param {Number} ms - Delay in milliseconds.
   * **Called by:
   * @see {calculateParabolicModel} - to let the "calculating" cursor render before the heavy work runs.
   * @memberof module:Mepbay
   * @returns {Promise<void>}
   */
  const timer = (ms) => new Promise((res) => setTimeout(res, ms));

  /**
   * @function calculateParabolicModel
   * @description Computes the parabolic bay-shape model from the layer's three control points and
   * draws its results (auxiliary point, beta angle arc, R0 label, and the parabola itself) on the
   * map.
   * @param {Object} diffractionPoint - The diffraction point coordinates {lat, lng}.
   * @param {Object} closurePoint - The closure point coordinates {lat, lng}.
   * @param {Object} tangentPoint - The tangent point coordinates {lat, lng}.
   * @param {String} layerId - The ID of the layer to calculate for.
   * @see {@link calculateWithTurf}
   * @see {@link createMarkerIcon}
   * @see {@link drawAngleAndR0}
   * **Called by:
   * @see {addDragListeners} - on drag end, once all three points exist.
   * @see {handleMapClick} - after placing the final control point.
   * @memberof module:Mepbay
   * @returns {Promise<void>}
   */
  const calculateParabolicModel = async (diffractionPoint, closurePoint, tangentPoint, layerId) => {
    const layer = layersRef.current[layerId];
    if (!layer) return;

    setIsCalculating(true);

    await timer(50);

    try {
      const results = calculateWithTurf(diffractionPoint, closurePoint, tangentPoint);

      const auxiliaryIcon = createMarkerIcon('auxiliaryPoint', layer, layer.opacity);

      const auxiliaryMarker = new window.google.maps.Marker({
        position: {
          lat: results.newPointWGS84.lat,
          lng: results.newPointWGS84.lng,
        },
        map: mapRef.current,
        title: t('forms.mepbay.result.auxiliaryPoint'),
        draggable: false,
        icon: auxiliaryIcon,
      });

      layer.markers['auxiliaryPoint'] = auxiliaryMarker;

      const lineToAuxPoint = new window.google.maps.Polyline({
        path: [
          { lat: diffractionPoint.lat, lng: diffractionPoint.lng },
          { lat: results.newPointWGS84.lat, lng: results.newPointWGS84.lng },
        ],
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: layer.opacity * 0.8,
        strokeWeight: 3,
        visible: layer.visible,
      });
      lineToAuxPoint.setMap(mapRef.current);
      layer.lines.push(lineToAuxPoint);

      const parabolaLine = new window.google.maps.Polyline({
        path: results.parabolaPoints,
        geodesic: true,
        strokeColor: layer.color,
        strokeOpacity: 0,
        strokeWeight: 3,
        visible: layer.visible,
        redLine: true,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: layer.opacity * 0.7,
              strokeColor: layer.color,
              scale: 4,
            },
            offset: '0',
            repeat: '20px',
          },
        ],
      });

      parabolaLine.setMap(mapRef.current);
      layer.lines.push(parabolaLine);

      drawAngleAndR0({
        layer,
        mapRef,
        diffPoint: diffractionPoint,
        clsPoint: closurePoint,
        auxPoint: results.newPointWGS84,
        beta: results.beta,
      });

      layer.parabolicResults = {
        thetaValues: results.thetaValues,
        C0: results.C0,
        C1: results.C1,
        C2: results.C2,
        R0: results.R0,
        beta: results.beta,
        cosBetaConf: results.cosBetaConf,
        pointsCount: results.parabolaPoints.length,
        linePoints: results.parabolaPoints,
      };

      layer.completed = true;
      setLayers((prev) => ({
        ...prev,
        [layerId]: {
          ...prev[layerId],
          completed: true,
          parabolicResults: layer.parabolicResults,
        },
      }));

      setIsCalculating(false);
    } catch (error) {
      console.error(t('forms.mepbay.result.errorCalculatingModel'), error);
      layer.completed = true;
      setLayers((prev) => ({
        ...prev,
        [layerId]: { ...prev[layerId], completed: true },
      }));
      setIsCalculating(false);
    }
  };

  /**
   * @function restorePendingLayers
   * @description Picks up layer data left by a session import (see sessionExport.js's
   * PENDING_MEPBAY_STORAGE_KEY) and redraws each layer's control-point markers/lines, then reuses
   * calculateParabolicModel to recompute (pure client-side math, so re-running it is cheap) and
   * draw the parabola/angle/R0 visuals exactly as if the user had just placed the third point.
   * Restored markers are non-draggable: addDragListeners' drag handlers close over the
   * `activeLayerId` value from render time, so markers built here (outside any click handler, on
   * the very first render) would forever act on whatever layer was active back then — silently
   * dragging the wrong layer's points instead of their own. Not wiring drag avoids that; "Reset"
   * on the layer already lets the user redo its points from scratch if needed.
   * **Called by:
   * @see {initializeMap}
   * @memberof module:Mepbay
   * @returns {Promise<void>}
   */
  const restorePendingLayers = async () => {
    const pendingLayers = readPendingMepBaySession();
    if (!pendingLayers || !pendingLayers.length) return;

    let maxSuffix = 0;

    for (const savedLayer of pendingLayers) {
      const layerId = savedLayer.id;
      const suffixMatch = /_(\d+)$/.exec(layerId || '');
      if (suffixMatch) maxSuffix = Math.max(maxSuffix, Number(suffixMatch[1]));

      const points = savedLayer.controlPoints || {};
      const newLayer = {
        id: layerId,
        name: savedLayer.name,
        color: savedLayer.color,
        visible: savedLayer.visible !== false,
        opacity: savedLayer.opacity ?? 1,
        controlPoints: points,
        markers: {},
        lines: [],
        completed: false,
      };

      layersRef.current[layerId] = newLayer;
      setLayers((prev) => ({ ...prev, [layerId]: newLayer }));

      Object.entries(points).forEach(([pointKey, point]) => {
        if (!point) return;
        const icon = createMarkerIcon(pointKey, newLayer, newLayer.opacity);
        const marker = new window.google.maps.Marker({
          position: point,
          map: mapRef.current,
          draggable: false,
          icon,
        });
        newLayer.markers[pointKey] = marker;
      });

      drawLines(points, layerId);

      const pointsArray = Object.values(points).filter(Boolean);
      if (pointsArray.length === 3) {
        const [diffractionPoint, coastalControlPoint, predominantWavePoint] = pointsArray;
        await calculateParabolicModel(diffractionPoint, coastalControlPoint, predominantWavePoint, layerId);
      }

      if (savedLayer.visible === false) {
        Object.values(newLayer.markers).forEach((marker) => marker.setMap(null));
        newLayer.lines.forEach((line) => line.setMap(null));
      }
    }

    setLayerCounter((prev) => Math.max(prev, maxSuffix));
  };

  /**
   * @function createNewLayer
   * @description Creates a new layer for parabolic model visualization with a random color, and
   * sets it as the active layer.
   * **Called by:
   * @see {handleStartSelection} - when no layer is active yet.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const createNewLayer = () => {
    const colors = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#673ab7',
      '#3f51b5',
      '#2196f3',
      '#03a9f4',
      '#00bcd4',
      '#009688',
      '#4caf50',
      '#8bc34a',
      '#cddc39',
      '#ffeb3b',
      '#ffc107',
      '#ff9800',
      '#ff5722',
    ];
    const newLayerId = `layer_${layerCounter + 1}`;
    const newLayer = {
      id: newLayerId,
      name: `${t('forms.mepbay.result.layerName')} ${layerCounter + 1}`,
      color: colors[Math.floor(Math.random() * colors.length)],
      visible: true,
      opacity: 1.0,
      controlPoints: {
        diffractionPoint: null,
        coastalControlPoint: null,
        predominantWavePoint: null,
      },
      markers: {},
      lines: [],
      completed: false,
    };

    layersRef.current[newLayerId] = newLayer;
    setLayers((prev) => ({ ...prev, [newLayerId]: newLayer }));
    setActiveLayerId(newLayerId);
    setLayerCounter((prev) => prev + 1);
    setControlPoints(newLayer.controlPoints);
    setCurrentStep(0);
    setIsSelectingPoint(false);
  };

  /**
   * @function handleStartSelection
   * @description Initiates point selection for a specific step in the parabolic model workflow,
   * creating a new layer first if none is active yet.
   * @param {Number} stepIndex - The index of the step to start selecting (0=diffraction, 1=closure, 2=tangent).
   * @see {@link createNewLayer}
   * **Called by:
   * @see {ProcessingPage} - via SidePanel's step "Select" buttons.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleStartSelection = (stepIndex) => {
    if (!activeLayerId) {
      createNewLayer();
      return;
    }

    setCurrentStep(stepIndex);
    setIsSelectingPoint(true);
  };

  /**
   * @function resetActiveLayer
   * @description Resets the active layer by clearing all its markers, lines, and control points,
   * so the user can redo the point selection from scratch.
   * **Called by:
   * @see {ProcessingPage} - via SidePanel's "Reset" action.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const resetActiveLayer = () => {
    if (!activeLayerId) return;

    const layer = layersRef.current[activeLayerId];
    if (!layer) return;

    Object.values(layer.markers).forEach((marker) => marker.setMap(null));
    layer.markers = {};

    layer.lines.forEach((line) => line.setMap(null));
    layer.lines = [];

    if (layer.r0Overlay) {
      layer.r0Overlay.setMap(null);
      layer.r0Overlay = null;
    }

    layer.controlPoints = {
      diffractionPoint: null,
      coastalControlPoint: null,
      predominantWavePoint: null,
    };
    layer.completed = false;

    setControlPoints({
      diffractionPoint: null,
      coastalControlPoint: null,
      predominantWavePoint: null,
    });
    setCurrentStep(0);
    setIsSelectingPoint(false);

    setLayers((prev) => ({
      ...prev,
      [activeLayerId]: { ...prev[activeLayerId], ...layer },
    }));
  };

  /**
   * @function createNewLayerForSidePanel
   * @description Creates a new layer with a random color and sets it active, without resetting
   * the current wizard step (unlike `createNewLayer`). Passed to SidePanel as its layer-creation
   * callback.
   * **Called by:
   * @see {SidePanel}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const createNewLayerForSidePanel = () => {
    const colors = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#673ab7',
      '#3f51b5',
      '#2196f3',
      '#03a9f4',
      '#00bcd4',
      '#009688',
      '#4caf50',
      '#8bc34a',
      '#cddc39',
      '#ffeb3b',
      '#ffc107',
      '#ff9800',
      '#ff5722',
    ];
    const newLayerId = `layer_${layerCounter + 1}`;
    const newLayer = {
      id: newLayerId,
      name: `${t('forms.mepbay.result.layerName')} ${layerCounter + 1}`,
      color: colors[Math.floor(Math.random() * colors.length)],
      visible: true,
      opacity: 1.0,
      controlPoints: {
        diffractionPoint: null,
        coastalControlPoint: null,
        predominantWavePoint: null,
      },
      markers: {},
      lines: [],
      completed: false,
    };

    layersRef.current[newLayerId] = newLayer;
    setLayers((prev) => ({ ...prev, [newLayerId]: newLayer }));
    setActiveLayerId(newLayerId);
    setLayerCounter((prev) => prev + 1);
    setControlPoints(newLayer.controlPoints);
    setCurrentStep(0);
    setIsSelectingPoint(false);
  };

  /**
   * @function deleteLayerForSidePanel
   * @description Deletes a layer and removes all its associated map elements (overlay, ROI
   * polygon, or markers/lines), selecting another remaining layer as active (or none). Passed to
   * SidePanel as its layer-deletion callback.
   * @param {String} layerId - The ID of the layer to delete.
   * **Called by:
   * @see {SidePanel}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const deleteLayerForSidePanel = (layerId) => {
    const layer = layersRef.current[layerId];
    if (layer) {
      if (layer.overlay) {
        layer.overlay.setMap(null);
      } else if (layer.isROI && layer.polygon) {
        layer.polygon.setMap(null);
      } else {
        Object.values(layer.markers).forEach((marker) => marker.setMap(null));
        layer.lines.forEach((line) => line.setMap(null));
        if (layer.r0Overlay) {
          layer.r0Overlay.setMap(null);
          layer.r0Overlay = null;
        }
      }
      delete layersRef.current[layerId];
      setLayers((prev) => {
        const updated = { ...prev };
        delete updated[layerId];
        return updated;
      });

      if (activeLayerId === layerId) {
        const remainingLayers = Object.keys(layersRef.current).filter((id) => !layersRef.current[id].isROI);
        if (remainingLayers.length > 0) {
          setActiveLayerId(remainingLayers[0]);
          setControlPoints(layersRef.current[remainingLayers[0]].controlPoints);
        } else {
          setActiveLayerId(null);
          setControlPoints({
            diffractionPoint: null,
            coastalControlPoint: null,
            predominantWavePoint: null,
          });
        }
      }
    }
  };

  /**
   * @function updateLayerColorForSidePanel
   * @description Updates a layer's color and all its associated visual elements (ROI polygon,
   * lines, and marker icons). Passed to SidePanel as its layer-recolor callback.
   * @param {String} layerId - The ID of the layer to update.
   * @param {String} color - The new color, in hex format.
   * @see {@link createMarkerIcon}
   * **Called by:
   * @see {SidePanel}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const updateLayerColorForSidePanel = (layerId, color) => {
    const layer = layersRef.current[layerId];
    if (layer) {
      layer.color = color;
      if (layer.isROI && layer.polygon) {
        layer.polygon.setOptions({
          fillColor: color,
          strokeColor: color,
        });
      } else if (layer.lines) {
        layer.lines.forEach((line) => {
          if (line.redLine && line.icons) {
            line.setOptions({
              strokeColor: color,
              icons: [
                {
                  icon: {
                    path: 'M 0,-1 0,1',
                    strokeOpacity: layer.opacity * 0.7,
                    strokeColor: color,
                    scale: 4,
                  },
                  offset: '0',
                  repeat: '20px',
                },
              ],
            });
          } else {
            line.setOptions({
              strokeColor: color,
            });
          }
        });
      }
      if (layer.markers) {
        Object.entries(layer.markers).forEach(([pointKey, marker]) => {
          if (marker && marker.setIcon) {
            const icon = createMarkerIcon(pointKey, layer, layer.opacity);
            if (icon) {
              marker.setIcon(icon);
            }
          }
        });
      }
      setLayers((prev) => ({
        ...prev,
        [layerId]: { ...prev[layerId], color },
      }));
    }
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current AOI/satellite selection, acquired
   * imagery, and drawn parabolic-model layers, for MepBay's "import session" icon to replay later
   * — a developer/testing shortcut so QA doesn't have to redo the whole wizard every time they
   * need a completed result to test against.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {NavigationBar} - via the standardized "export session for testing" icon's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(buildSessionExport({ coordinates, satelliteIndex, availableDates, images, layers }));
  };
  const hasExportableLayers = Object.values(layers).some((layer) => !layer.isROI);
  useRegisterSessionExport('mepbay', hasExportableLayers ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes every fitted bay - its parabolic curve, control points and control lines - plus the area
   * of interest, to a GeoJSON file for a GIS.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (!exportGeoJson({ coordinates, layers })) notifyError(t('forms.acquisition.geoJsonExport.empty'));
  };
  useRegisterGeoJsonExport('mepbay', hasExportableLayers ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildMepBayReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.mepbay.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, () => displayROI(mapRef.current));
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildMepBayReport({
        coordinates,
        layers,
        images: [screen],
        text,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, hasExportableLayers ? exportPdfReport : null);


  return (
    <ThemeProvider theme={mepbayTheme}>
    <Box className={classes.wrapper}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className={classes.leftStack}>
        <OverlayPanelsToggle side="left" />
      <LayerControler
        layers={layers}
        activeLayerId={activeLayerId}
        layersRef={layersRef}
        mapRef={mapRef}
        setLayers={setLayers}
        setActiveLayerId={setActiveLayerId}
        setLayerCounter={setLayerCounter}
        layerCounter={layerCounter}
        setControlPoints={setControlPoints}
        setCurrentStep={setCurrentStep}
        setIsSelectingPoint={setIsSelectingPoint}
      />

      <SidePanel
        mapSwitcher={
          <BackgroundMapPanel
            mapType={mapType}
            onMapTypeChange={handleMapTypeChange}
            labelsVisible={labelsVisible}
            onLabelsVisibleChange={setLabelsVisible}
            t={t}
          />
        }
        layers={layers}
        activeLayerId={activeLayerId}
        controlPoints={controlPoints}
        isCalculating={isCalculating}
        isSelectingPoint={isSelectingPoint}
        currentStep={currentStep}
        steps={steps}
        handleStartSelection={handleStartSelection}
        resetActiveLayer={resetActiveLayer}
        setModelModalOpen={setModelModalOpen}
        setViewDataMenuAnchor={setViewDataMenuAnchor}
        viewDataMenuAnchor={viewDataMenuAnchor}
        modelModalOpen={modelModalOpen}
        layersRef={layersRef}
        mapRef={mapRef}
        createNewLayer={createNewLayerForSidePanel}
        deleteLayer={deleteLayerForSidePanel}
        updateLayerColor={updateLayerColorForSidePanel}
        setActiveLayerId={setActiveLayerId}
      />
      </div>

      <div className={`${classes.mapContainer} ${isSelectingPoint ? classes.selectingMode : ''}`}>
        {isCalculating && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              flexDirection: 'column',
            }}>
            <div
              style={{
                backgroundColor: 'white',
                padding: '24px 32px',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #e3f2fd',
                  borderTop: '4px solid #2196f3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <Typography variant="h6" style={{ color: '#2196f3', fontWeight: 'bold' }}>
                {t('forms.mepbay.result.calculating')}
              </Typography>
              <Typography
                variant="body2"
                style={{
                  color: '#666',
                  textAlign: 'center',
                  maxWidth: '300px',
                }}>
                {t('forms.mepbay.result.calculating')}
              </Typography>
            </div>
          </div>
        )}

        <div id="google-maps" ref={(r) => (mapDiv.current = r)} style={{ width: '100%', height: '100%' }} />
      </div>

      <ReturnButtons lastStepPath="/main/mepbay/3" />
    </Box>
    </ThemeProvider>
  );
};
/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay processing page.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Mepbay
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
    overflow: 'hidden',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  leftStack: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 1001,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    maxHeight: `calc(100% - ${theme.spacing(2)}px - ${OVERLAY_BOTTOM_SAFE_AREA})`,
    overflowY: 'auto',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  sidePanel: {
    width: 400,
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    overflowY: 'auto',
    maxHeight: '100vh',
  },
  stepCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  completedStep: {
    backgroundColor: '#e8f5e8',
    borderLeft: '4px solid #4caf50',
  },
  activeStep: {
    backgroundColor: '#fff3e0',
    borderLeft: '4px solid #ff9800',
  },
  inactiveStep: {
    backgroundColor: '#f5f5f5',
    borderLeft: '4px solid #ccc',
  },
  coordinatesText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: '#666',
    marginTop: theme.spacing(1),
  },
  selectingMode: {
    '& *': {
      cursor: 'crosshair !important',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: '3px solid #ff9800',
      borderRadius: '4px',
      pointerEvents: 'none',
      zIndex: 1000,
      animation: '$pulse 2s infinite',
    },
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  floatingButton: {
    position: 'absolute',
    zIndex: 1000,
    backgroundColor: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  addImageButton: {
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  addPointsButton: {
    top: theme.spacing(10),
    left: theme.spacing(2),
  },
  viewDataButton: {
    top: theme.spacing(18),
    left: theme.spacing(2),
  },
  layersMenu: {
    '& .MuiPaper-root': {
      minWidth: 300,
      maxHeight: 400,
      overflowY: 'auto',
    },
  },
  layerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid #eee',
  },
  layerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  opacitySlider: {
    width: 80,
    marginLeft: theme.spacing(1),
  },
  activeLayerIndicator: {
    backgroundColor: '#e3f2fd',
    borderLeft: '4px solid #2196f3',
  },
}));
export default ProcessingPage;
